import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Globe2 } from "lucide-react";

/**
 * CountryTickerTile — animated "14+ African countries represented" tile.
 * Cycles country names with their iconic signature image so the hero feels alive.
 */

const COUNTRIES = [
  { flag: "🇳🇬", name: "Nigeria",       img: "https://images.unsplash.com/photo-1592402927625-528eafd6cae8?auto=format&fit=crop&q=80&w=400", hint: "Lagos skyline" },
  { flag: "🇬🇭", name: "Ghana",         img: "https://images.unsplash.com/photo-1524414621493-7dec026782c3?auto=format&fit=crop&q=80&w=400", hint: "Kente cloth" },
  { flag: "🇰🇪", name: "Kenya",         img: "https://images.unsplash.com/photo-1582142306909-195724d33da3?auto=format&fit=crop&q=80&w=400", hint: "Maasai beads" },
  { flag: "🇿🇦", name: "South Africa",  img: "https://images.unsplash.com/photo-1580060839134-75a5edca2e99?auto=format&fit=crop&q=80&w=400", hint: "Table Mountain" },
  { flag: "🇸🇳", name: "Senegal",       img: "https://images.unsplash.com/photo-1591375372181-94e2c44a90c1?auto=format&fit=crop&q=80&w=400", hint: "Dakar market" },
  { flag: "🇪🇹", name: "Ethiopia",      img: "https://images.unsplash.com/photo-1495474472287-4d71bcdd2085?auto=format&fit=crop&q=80&w=400", hint: "Ethiopian coffee" },
  { flag: "🇪🇬", name: "Egypt",         img: "https://images.unsplash.com/photo-1539650116574-75c0c6d73f9c?auto=format&fit=crop&q=80&w=400", hint: "Pyramids of Giza" },
  { flag: "🇹🇿", name: "Tanzania",      img: "https://images.unsplash.com/photo-1547471080-7cc2caa01a7e?auto=format&fit=crop&q=80&w=400", hint: "Tinga Tinga art" },
  { flag: "🇨🇲", name: "Cameroon",      img: "https://images.unsplash.com/photo-1611080626919-7cf5a9dbab5b?auto=format&fit=crop&q=80&w=400", hint: "Cameroonian craft" },
  { flag: "🇨🇮", name: "Côte d'Ivoire", img: "https://images.unsplash.com/photo-1481391319762-47dff72954d9?auto=format&fit=crop&q=80&w=400", hint: "Cocoa heritage" },
  { flag: "🇲🇦", name: "Morocco",       img: "https://images.unsplash.com/photo-1539020140153-e479b8c25b15?auto=format&fit=crop&q=80&w=400", hint: "Marrakech medina" },
  { flag: "🇺🇬", name: "Uganda",        img: "https://images.unsplash.com/photo-1559827260-dc66d52bef19?auto=format&fit=crop&q=80&w=400", hint: "Pearl of Africa" },
  { flag: "🇿🇼", name: "Zimbabwe",      img: "https://images.unsplash.com/photo-1626085018034-4f9e0b8c8e0f?auto=format&fit=crop&q=80&w=400", hint: "Shona sculpture" },
  { flag: "🇷🇼", name: "Rwanda",        img: "https://images.unsplash.com/photo-1591375372181-94e2c44a90c1?auto=format&fit=crop&q=80&w=400", hint: "Kigali market" },
];

export const CountryTickerTile = () => {
  const [idx, setIdx] = useState(0);
  const [reducedMotion, setReducedMotion] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia?.("(prefers-reduced-motion: reduce)");
    if (mq?.matches) setReducedMotion(true);
  }, []);

  // Preload images so the rotation has no flash.
  useEffect(() => {
    COUNTRIES.forEach((c) => {
      const im = new Image();
      im.src = c.img;
    });
  }, []);

  useEffect(() => {
    if (reducedMotion) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % COUNTRIES.length), 2600);
    return () => clearInterval(t);
  }, [reducedMotion]);

  const current = COUNTRIES[idx];

  return (
    <div
      className="col-span-2 row-span-3 bg-[#2A3D36] rounded-lg p-5 text-white flex flex-col justify-between relative overflow-hidden"
      data-testid="country-ticker-tile"
    >
      {/* Background image of current country, blurred + dim, for richer feel */}
      <AnimatePresence>
        <motion.div
          key={`bg-${idx}`}
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.18 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.7 }}
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage: `url(${current.img})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            filter: "blur(2px)",
          }}
        />
      </AnimatePresence>

      <div className="relative z-10 flex items-center gap-2">
        <Globe2 className="w-6 h-6 opacity-80"/>
        <span className="text-[10px] uppercase tracking-widest text-white/70 font-medium">Live</span>
      </div>

      <div className="relative z-10">
        <div className="text-4xl sm:text-5xl font-display leading-none">14+</div>
        <div className="text-[11px] text-white/70 mt-1 mb-3 uppercase tracking-wide">African countries</div>

        {/* Animated row: signature image + flag + country name */}
        <div className="h-14 relative overflow-hidden" data-testid="country-ticker">
          <AnimatePresence mode="popLayout">
            <motion.div
              key={idx}
              initial={{ y: 24, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: -24, opacity: 0 }}
              transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              className="absolute inset-0 flex items-center gap-3"
            >
              <div className="shrink-0 w-12 h-12 rounded-full overflow-hidden border-2 border-[#F2C94C]/50 shadow-md">
                <img
                  src={current.img}
                  alt={current.hint}
                  className="w-full h-full object-cover"
                  loading="eager"
                />
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-1.5 text-base font-display italic text-[#F2C94C] leading-tight">
                  <span className="text-lg leading-none">{current.flag}</span>
                  <span className="truncate">{current.name}</span>
                </div>
                <div className="text-[10px] text-white/60 truncate">{current.hint}</div>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

export default CountryTickerTile;
